// src/lib/cta.ts
// Centralized CTA labels (EN/FR) + tiny helper

export type Lang = 'en' | 'fr'
export type Audience =
  | 'seller'
  | 'buyer'
  | 'broker'
  | 'lender'
  | 'consultant'
  | 'partner'

export type CTATexts = {
  primary: Record<Lang, string>
  secondary: Record<Lang, string>
  ariaLabel?: Record<Lang, string>
}

export const CTA_MAP: Record<Audience, CTATexts> = {
  seller: {
    primary: { en: 'Start my listing', fr: 'Démarrer mon listing' },
    secondary: { en: 'Contact the team', fr: "Contacter l'équipe" },
    ariaLabel: { en: 'Contact the team — Sellers', fr: "Contacter l'équipe — Vendeurs" },
  },
  buyer: {
    primary: { en: 'Sign up as a buyer', fr: "S'inscrire comme acheteur" },
    secondary: { en: 'Contact the team', fr: "Contacter l'équipe" },
    ariaLabel: { en: 'Contact the team — Buyers', fr: "Contacter l'équipe — Acheteurs" },
  },
  broker: {
    primary: { en: 'Create broker listing', fr: 'Créer un listing courtier' },
    secondary: { en: 'Contact the team', fr: "Contacter l'équipe" },
    ariaLabel: { en: 'Contact the team — Brokers', fr: "Contacter l'équipe — Courtiers" },
  },
  lender: {
    primary: { en: 'Join the network', fr: 'Rejoindre le réseau' },
    secondary: { en: 'Contact the team', fr: "Contacter l'équipe" },
    ariaLabel: { en: 'Contact the team — Lenders', fr: "Contacter l'équipe — Prêteurs" },
  },
  consultant: {
    primary: { en: 'Become a Consultant', fr: 'Devenir consultant' },
    secondary: { en: 'Contact the team', fr: "Contacter l'équipe" },
    ariaLabel: { en: 'Contact the team — Consultants', fr: "Contacter l'équipe — Consultants" },
  },
  partner: {
    primary: { en: 'Join the network', fr: 'Rejoindre le réseau' },
    secondary: { en: 'Contact the team', fr: "Contacter l'équipe" },
    ariaLabel: { en: 'Contact the team — Partners', fr: "Contacter l'équipe — Partenaires" },
  },
}

// Helper to pick texts for any audience/lang
export function cta(audience: Audience, lang: Lang = 'en') {
  const node = CTA_MAP[audience]
  return {
    primary: node.primary[lang],
    secondary: node.secondary[lang],
    aria: node.ariaLabel?.[lang] ?? (lang === 'fr' ? "Contacter l'équipe" : 'Contact the team'),
  }
}

// Optional: styled CTA components (Tailwind)
export const CTA_STYLES = {
  primary: 'rounded-2xl px-5 py-2 text-sm font-semibold text-gray-900',
  secondary: 'rounded-2xl border px-5 py-2 text-sm font-semibold hover:bg-white/10',
}
export const CTA_COLORS = {
  goldStart: '#C9A033',
  goldEnd: '#EAD67C',
}
